import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { Provider, useDispatch } from "react-redux";
import { ConfigProvider } from "@arco-design/web-react";
import enUS from "@arco-design/web-react/es/locale/en-US";
import axios from "axios";

import { AnalyticsIdentitySync } from "./analytics/AnalyticsIdentitySync";
import { AnalyticsRouteListener } from "./analytics/AnalyticsRouteListener";
import { endpoints } from "./api/endpoints";
import { SyncUserLanguage } from "./components/SyncUserLanguage/SyncUserLanguage";
import { GuestConversionModalHost } from "./components/GuestRoleConversionModal/GuestConversionModalHost";
import { MentorAvailabilityReminder } from "./components/MentorAvailabilityReminder/MentorAvailabilityReminder";
import { AppLayout } from "./Layout/Layout";
import { Pages } from "./pages/Pages";
import { routes } from "./routes/routes";
import { store } from "./store/store";
import { setUser, setUserLanguage } from "./store/userSlice";

import "./App.css";

const PASSWORD_RESET_PATHS: string[] = [
  routes.RESETPASSWORD,
  routes.CONFIRM_RESET_PASSWORD,
];

const WhoAmIWrapper = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const skipSessionRefresh = PASSWORD_RESET_PATHS.includes(location.pathname);

  useEffect(() => {
    if (skipSessionRefresh) {
      return;
    }

    const storedUser = localStorage.getItem("user");

    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      dispatch(setUser(parsed));
      const locale = (parsed.language === 'en' || parsed.language === 'es') ? parsed.language : 'en';
      dispatch(setUserLanguage(locale));
    }

    // Always refresh from the API so role changes (e.g. isSuperAdmin) aren't stale.
    axios
      .get(endpoints.whoami)
      .then((response) => {
        dispatch(setUser({ ...response.data }));
      })
      .catch((error) => {
        console.error("whoami refresh failed:", error);
      });
  }, [dispatch, skipSessionRefresh]);

  return <>{children}</>;
};

function App() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  const isIos = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  };
  // Detects if device is in standalone mode (already using PWA)
  const isInStandaloneMode = () =>
    "standalone" in window.navigator && window.navigator.standalone;

  // Checks if should display install popup notification:
  if (isIos() && !isInStandaloneMode()) {
    // Message.info(
    //   "To install app on your Iphone: tap shae button and then press 'Add to Home Screen'."
    // );
  }

  useEffect(() => {
    // Listen for the 'beforeinstallprompt' event
    window.addEventListener("beforeinstallprompt", (event) => {
      // Prevent the mini-infobar from appearing on mobile
      // event.preventDefault(); // commented to show it nativly
      // Save the event so it can be triggered later
      setDeferredPrompt(event);
    });
  }, []);

  const handleInstallClick = () => {
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      // Wait for the user to respond to the prompt
      deferredPrompt.userChoice.then((choiceResult: { outcome: string }) => {
        if (choiceResult.outcome === "accepted") {
          console.log("User accepted the A2HS prompt");
        } else {
          console.log("User dismissed the A2HS prompt");
        }
        // Clear the deferredPrompt state variable
        setDeferredPrompt(null);
      });
    }
  };

  useEffect(() => {
    const reloadOnChunkError = (message: string) => {
      if (!message.includes("ChunkLoadError") && !message.includes("Loading chunk")) {
        return;
      }
      const reloadKey = "werule-chunk-reload";
      if (sessionStorage.getItem(reloadKey)) {
        return;
      }
      sessionStorage.setItem(reloadKey, "1");
      window.location.reload();
    };

    const onError = (event: ErrorEvent) => {
      reloadOnChunkError(event.message ?? "");
      if (event.message.startsWith("ResizeObserver")) {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div"
        );
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay"
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute("style", "display: none");
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute("style", "display: none");
        }
      }
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message =
        typeof reason === "string"
          ? reason
          : reason instanceof Error
            ? reason.message
            : "";
      reloadOnChunkError(message);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return (
    <>
      {/*{!!deferredPrompt && (*/}
      {/* TODO: please replace this with a proper message popup */}
      {/*  <button onClick={handleInstallClick}>Install App</button>*/}
      {/*)}*/}
      <Provider store={store}>
        <Router>
          <WhoAmIWrapper>
            <AnalyticsRouteListener />
            <AnalyticsIdentitySync />
            <SyncUserLanguage />
            <GuestConversionModalHost />
            <MentorAvailabilityReminder />
            <ConfigProvider locale={enUS}>
              <AppLayout>
                <Pages />
              </AppLayout>
            </ConfigProvider>
          </WhoAmIWrapper>
        </Router>
      </Provider>
    </>
  );
}

export default App;
